import {
  Survey,
  locationUploadFromSurvey,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
  surveyToMarginForm,
  SurveyMarginFormState,
  surveyToMetadataForm,
} from '@ps/survey-utils';

import { SuppliersMetaData } from '../survey-creation-suppliers-meta-data-state.model';
import { DEFAULT_CHILD_AUDIENCE_STATE } from '../../survey-creation-state.constants';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';

export function buildMetadataFormStateFromSurvey(
  survey: Survey,
): SurveyMetadataFormState {
  return {
    value: surveyToMetadataForm(survey),
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildMarginFormStateFromSurvey(
  survey: Survey,
): SurveyMarginFormState {
  return {
    value: surveyToMarginForm(survey),
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildQualificationsFormStateFromSurvey(
  survey: Survey,
): SurveyQualificationsFormState {
  return {
    value: survey.qualifications ?? [],
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildQuotasFormStateFromSurvey(
  survey: Survey,
): SurveyQuotasFormState {
  return {
    value: survey.quotas ?? [],
    valid: true,
    errors: [],
    isDirty: false,
  };
}

// surveyPauseThreshold seeds from the loaded survey; the flexibility-prices load
// and apply-mirror effect still overwrite it when they land. manualOverride is not
// on `Survey`, so it seeds false and is filled by those same paths.
export function buildSuppliersMetaDataFromSurvey(
  survey: Survey,
): SuppliersMetaData {
  return {
    blendId: survey.blendId,
    priceOverride: survey.cpi?.priceOverride || null,
    defaultPurePriceOverride: !!survey.defaultPurePriceOverride,
    isPurePriceRateCardEnable: survey.isPurePriceRateCardEnable,
    supplierDefaultAllocation: survey.supplierDefaultAllocation,
    surveyPauseThreshold: survey.surveyPauseThreshold ?? null,
    manualOverride: false,
  };
}

export type HydratedChildAudienceState = Pick<
  SurveyCreationChildAudienceState,
  | 'advancedFeasibilityFilters'
  | 'metadata'
  | 'margin'
  | 'qualifications'
  | 'quotas'
  | 'suppliersMetaData'
  | 'locationFileUpload'
>;

export function hydrateChildFromSurvey(
  survey: Survey,
): HydratedChildAudienceState {
  return {
    advancedFeasibilityFilters: survey.advancedFeasibilityFilters,
    metadata: buildMetadataFormStateFromSurvey(survey),
    margin: buildMarginFormStateFromSurvey(survey),
    qualifications: buildQualificationsFormStateFromSurvey(survey),
    quotas: buildQuotasFormStateFromSurvey(survey),
    suppliersMetaData: buildSuppliersMetaDataFromSurvey(survey),
    locationFileUpload: locationUploadFromSurvey(survey),
  };
}

export function buildLoadedChild(
  survey: Survey,
): SurveyCreationChildAudienceState {
  return {
    ...DEFAULT_CHILD_AUDIENCE_STATE,
    ...hydrateChildFromSurvey(survey),
  };
}

export function keyedLoadedChildren(
  surveys: readonly Survey[],
): Record<number, SurveyCreationChildAudienceState> {
  return Object.fromEntries(
    surveys.map((survey) => [survey.id, buildLoadedChild(survey)]),
  );
}

export function buildLoadedRegistryChild(
  survey: Survey,
): SurveyChildRegistryState {
  return { survey, error: null, participant: true };
}

export function keyedLoadedRegistryChildren(
  surveys: readonly Survey[],
): Record<number, SurveyChildRegistryState> {
  return Object.fromEntries(
    surveys.map((survey) => [survey.id, buildLoadedRegistryChild(survey)]),
  );
}
